<template>
  <div id="map" :class="{ loading: loading }">
    <layers-loader :items="layersLoader" />
    <map-zoom-level :zoom-level="mapZoom" style="left: 9px" />
    <v-progress-circular
      class="map__loader"
      color="primary"
      :width="7"
      v-if="loading"
      indeterminate
      size="64"
    />
  </div>
</template>
<script>
import Feature from 'ol/Feature';
import Map from 'ol/Map';
import Point from 'ol/geom/Point';
import { Style, Stroke, Fill, Text, Icon } from 'ol/style';
import View from 'ol/View';
import Collection from 'ol/Collection';
import TileLayer from 'ol/layer/Tile';
import TileGrid from 'ol/tilegrid/TileGrid';
import TileWMS from 'ol/source/TileWMS';

import { containsExtent } from 'ol/extent.js';
import { fromExtent } from 'ol/geom/Polygon';
import { METERS_PER_UNIT } from 'ol/proj';
import { Translate } from 'ol/interaction.js';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource, Cluster } from 'ol/source';
import { defaults as defaultControls, ScaleLine } from 'ol/control.js';
import { get as getProjection } from 'ol/proj';
import { getWidth } from 'ol/extent';

import { getFeatureCentroid } from '@/utils/mapUtils';
import mapTools from '@/mixins/mapTools';
import layersLoaderMixin from '@/mixins/layersLoaderMixin';

import vuetify from '@/plugins/vuetify.js';

const vuetifyColors = vuetify.preset.theme.themes.light;

export default {
  mixins: [mapTools, layersLoaderMixin],
  components: {
    LayersLoader: () => import('@/components/LayersLoader'),
    MapZoomLevel: () => import('@/components/MapZoomLevel')
  },
  computed: {
    mapZoom() {
      if (this.map) {
        return this.map.getView().getZoom();
      }
      return 0;
    }
  },
  data: () => ({
    map: undefined,
    scale: undefined,
    wmsCadastralPrintLayers: [
      {
        id: 1,
        name_pl: 'KIEG działki i budynki',
        name_en: 'KIEG parcels and buildings',
        wms_layers: 'numery_dzialek,dzialki,budynki',
        wms_srs: 'EPSG:3857',
        wms_url: 'https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaEwidencjiGruntow'
      },
      {
        id: 2,
        name_pl: 'KIUT dane zasadnicze',
        name_en: 'KIUT cadastral data',
        wms_layers:
          'przewod_wodociagowy,przewod_kanalizacyjny,przewod_cieplowniczy,przewod_gazowy,przewod_telekomunikacyjny,przewod_elektroenergetyczny,przewod_benzynowy,przewod_naftowy,przewod_inny,przewod_slupy,przewod_urzadzenia,przewod_pozostale',
        wms_srs: 'EPSG:3857',
        wms_url: 'http://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaUzbrojeniaTerenu'
      }
    ]
  }),
  props: {
    clusteredFeatures: {
      type: Array,
      default: () => {
        return [];
      }
    },
    loading: {
      type: Boolean,
      default: () => {
        return false;
      }
    },
    parentMap: {
      required: true,
      validator: value => {
        return value === undefined || value instanceof Map;
      }
    },
    printedFeaturesProperties: {
      type: Object,
      default: () => {
        return {};
      }
    },
    printingType: {
      type: String,
      default: ''
    }
  },
  methods: {
    addExtentToMap(paperSize) {
      this.getLayerByName('printExtent')
        .getSource()
        .clear();
      if (!this.scale) {
        this.scale = this.calculateScale();
      }
      const boundingBox = this.calculateBoundingBox(this.map.getView().getCenter(), paperSize);
      this.drawBox(boundingBox);
    },
    addFeaturePropertiesToGroup(feature, object = this.printedFeaturesProperties) {
      const filteredProps = this.getPropertiesWithoutGeometry(feature);
      object[feature.get('group')].features.push(filteredProps);
      return object[feature.get('group')].features.length;
    },
    calculateBoundingBox([x, y], paperSize) {
      const [pageWidth, pageHeight] = paperSize.map(p => {
        return p * parseFloat(this.scale);
      });
      const minx = x - (pageWidth - 40) / (this.printingType === 'basic' ? 3 : 2);
      const paperMapHeight = this.printingType === 'basic' ? pageHeight / 2 - 10 : (pageHeight / 2 - 10) * 0.8;
      const miny = y - paperMapHeight;
      const maxx = x + (pageWidth - 40) / (this.printingType === 'basic' ? 3 : 2);
      const maxy = y + paperMapHeight;
      return [minx, miny, maxx, maxy];
    },
    calculateDPI() {
      const div = document.createElement('div');
      div.style.height = '1in';
      div.style.width = '1in';
      div.style.top = '-100%';
      div.style.left = '-100%';
      div.style.position = 'absolute';
      document.body.appendChild(div);
      const dpi = div.offsetHeight;
      document.body.removeChild(div);
      return dpi;
    },
    calculateScale() {
      const dpi = this.calculateDPI();
      const resolution = this.map.getView().getResolution();
      const unit = this.map
        .getView()
        .getProjection()
        .getUnits();
      const inchesPerMetre = 39.37;
      return parseInt(resolution * METERS_PER_UNIT[unit] * inchesPerMetre * dpi);
    },
    drawBox(bounds) {
      const feature = new Feature({
        geometry: fromExtent(bounds),
        style: new Style({
          fill: new Fill({ color: vuetifyColors.accent }),
          stroke: new Stroke({
            color: vuetifyColors.accent,
            width: 2
          })
        })
      });
      this.getLayerByName('printExtent')
        .getSource()
        .addFeature(feature);
      this.getInteractionByName('printExtentDragInteraction').setActive(true);
      const mapExtent = this.map.getView().calculateExtent(this.map.getSize());
      const featureExtent = feature.getGeometry().getExtent();
      if (!containsExtent(mapExtent, featureExtent)) {
        this.map.getView().fit(featureExtent, this.map.getSize());
      }
      this.$emit('drawBoxZoom', this.map.getView().getZoom());
    },
    getPaperExtent() {
      return this.getLayerByName('printExtent')
        .getSource()
        .getFeatures()[0]
        .getGeometry()
        .getExtent();
    },
    getBasicPrintLayers(featuresInExtent) {
      const preparedFeatures = this.prepareFeatures(featuresInExtent);
      let letter = 'A';
      const printLayer = new VectorLayer({
        name: 'printLayer',
        source: new Cluster({
          source: new VectorSource({}),
          distance: 200
        }),
        style: feature => {
          if (feature.getStyle()) {
            return feature.getStyle();
          } else {
            const features = feature.get('features');
            if (features.length === 1) {
              const number = String(this.addFeaturePropertiesToGroup(features[0])).padStart(2, '0');
              const style = new Style({
                text: new Text({
                  font: 'bold 20px sans-serif',
                  text: number,
                  offsetY: -12,
                  fill: new Fill({
                    color: features[0].get('color')
                  }),
                  backgroundFill: new Fill({
                    color: 'white'
                  })
                }),
                image: new Icon({
                  crossOrigin: 'anonymous',
                  src: require(`@/assets/marker_${features[0].get('color_name')}.png`),
                  rotateWithView: true
                })
              });
              feature.setStyle(style);
            } else {
              const style = new Style({
                text: new Text({
                  font: 'bold 25px sans-serif',
                  text: letter,
                  offsetY: -13,
                  offsetX: -1,
                  fill: new Fill({
                    color: vuetifyColors.accent
                  }),
                  backgroundFill: new Fill({
                    color: 'white'
                  })
                }),
                image: new Icon({
                  crossOrigin: 'anonymous',
                  src: require(`@/assets/marker_clustered.png`),
                  rotateWithView: true
                })
              });
              const featureToPush = {
                name: letter,
                color: '#004b35',
                features: {
                  builtUp: {
                    features: [],
                    color_name: 'accent',
                    color: vuetifyColors.accent
                  },
                  notBuiltUp: {
                    features: [],
                    color_name: 'accent',
                    color: vuetifyColors.accent
                  },
                  development: {
                    features: [],
                    color_name: 'accent',
                    color: vuetifyColors.accent
                  }
                }
              };
              features.forEach(f => {
                this.addFeaturePropertiesToGroup(f, featureToPush.features);
              });
              letter = String.fromCharCode(letter.charCodeAt() + 1);
              this.clusteredFeatures.push(featureToPush);
              feature.setStyle(style);
              return style;
            }
          }
        }
      });
      printLayer
        .getSource()
        .getSource()
        .addFeatures(preparedFeatures);
      return [printLayer];
    },
    getCadastralPrintLayers(featuresInExtent) {
      const printLayer = new VectorLayer({
        name: 'printLayer',
        source: new VectorSource({
          features: new Collection([]),
          wrapX: false
        }),
        style: feature => {
          const style = new Style({
            fill: new Fill({
              color: `${vuetifyColors.accent}30`
            }),
            stroke: new Stroke({
              color: `${vuetifyColors.accent}50`,
              width: 10
            })
          });
          return style;
        }
      });
      printLayer.getSource().addFeatures(featuresInExtent);

      const wmsPrintLayers = [];
      for (const layer of this.wmsCadastralPrintLayers) {
        let projExtent;
        if (getProjection(layer.wms_srs).getExtent()) {
          projExtent = getProjection(layer.wms_srs).getExtent();
        } else {
          projExtent = getProjection('EPSG:3857').getExtent();
        }
        let startResolution = getWidth(projExtent) / 512;
        let resolutions = new Array(22);
        for (let i = 0, ii = resolutions.length; i < ii; ++i) {
          resolutions[i] = startResolution / Math.pow(2, i);
        }
        const source = new TileWMS({
          crossOrigin: 'Anonymous',
          url: `${process.env.VUE_APP_DIVI_BASE_URL}/wms_proxy/${layer.wms_url}`,
          projection: layer.wms_srs,
          params: {
            LAYERS: layer.wms_layers,
            TILED: true,
            VERSION: '1.1.1'
          },
          tileGrid: new TileGrid({
            extent: projExtent,
            resolutions: resolutions,
            tileSize: [512, 512]
          })
        });
        const wmsLayer = new TileLayer({
          name: layer.name_pl,
          source
        });
        this.addLayersLoaderSource(layer.id, { pl: layer.name_pl, en: layer.name_en }, source);
        wmsPrintLayers.push(wmsLayer);
      }

      return [printLayer, ...wmsPrintLayers];
    },
    getPrintedLayers(printedLayerName) {
      const parentMapFeaturesSource = this.getLayerByName(printedLayerName).getSource();
      const featuresInExtent = [];
      parentMapFeaturesSource.forEachFeatureIntersectingExtent(this.getPaperExtent(), feature => {
        featuresInExtent.push(feature);
      });
      return this.printingType === 'basic'
        ? this.getBasicPrintLayers(featuresInExtent)
        : this.getCadastralPrintLayers(featuresInExtent);
    },
    getPropertiesWithoutGeometry(feature) {
      const propsWithoutGeometry = {};
      const properties = feature.getProperties();
      const filteredPropsKeys = Object.keys(properties).filter(key => key !== 'geometry');
      filteredPropsKeys.forEach(key => {
        propsWithoutGeometry[key] = properties[key];
      });
      return propsWithoutGeometry;
    },
    prepareFeatures(features) {
      let builtUpIndex = 1;
      let notBuiltUpIndex = 1;
      let developmentIndex = 1;
      return features.map((feature, index) => {
        const featureCopy = feature.clone();
        featureCopy.setGeometry(new Point(getFeatureCentroid(feature).geometry.coordinates));
        const propertyTypeValue = feature.get('rodzaj_nieruchomosci');
        const ownershipStatisValue = feature.get('wlasnosc');
        const isBuiltUp = propertyTypeValue && propertyTypeValue.en === 'Built-up property' ? true : false;
        const isDevelopment =
          ownershipStatisValue && ownershipStatisValue.find(v => v.en === 'Developer option') ? true : false;
        featureCopy.setProperties({
          isBuiltUp,
          isDevelopment
        });
        if (isDevelopment) {
          featureCopy.set('color_name', 'accent');
          featureCopy.set('color', vuetifyColors.accent);
          featureCopy.set('group', 'development');
          developmentIndex++;
        } else if (isBuiltUp) {
          featureCopy.set('color_name', 'accent');
          featureCopy.set('color', vuetifyColors.accent);
          featureCopy.set('group', 'builtUp');
          builtUpIndex++;
        } else {
          featureCopy.set('color_name', 'accent');
          featureCopy.set('color', vuetifyColors.accent);
          featureCopy.set('group', 'notBuiltUp');
          notBuiltUpIndex++;
        }
        return featureCopy;
      });
    },
    init() {
      this.map = new Map({
        layers: this.parentMap.getLayers(),
        target: 'map',
        view: this.parentMap.getView(),
        controls: defaultControls().extend([new ScaleLine()])
      });

      this.map.addLayer(
        new VectorLayer({
          name: 'printExtent',
          source: new VectorSource({})
        })
      );
      const printExtentDragInteraction = new Translate({
        layers: [this.getLayerByName('printExtent')]
      });
      printExtentDragInteraction.set('name', 'printExtentDragInteraction');
      this.map.addInteraction(printExtentDragInteraction);
      setTimeout(() => {
        this.map.updateSize();
      });
    }
  },
  mounted() {
    this.init();
  }
};
</script>
<style lang="scss" scoped>
.loading {
  opacity: 0.5;
}
::v-deep .ol-control button {
  background-color: var(--v-primary-base);
}
#map {
  height: 400px;
  width: 100%;
  position: relative;
  .map__loader {
    position: absolute;
    z-index: 1;
    left: 60%;
    top: 40%;
    z-index: 100;
  }
}
</style>